import React from 'react';
import { Node, Transforms } from 'slate';
import { HistoryEditor } from 'slate-history';
import { ReactEditor } from 'slate-react';
import { ReactComponent as LayoutIcon } from 'assets/images/multiple-columns-icon.svg';
import { ReactComponent as ContentIcon } from 'assets/images/bullet-list-toggle-icon.svg';
import { ReactComponent as StyleIcon } from 'assets/images/brush-icon.svg';
import { ReactComponent as ScrollingIcon } from 'assets/images/node-menu-width-height-icon.svg';
import Expandable from './menu-item';
import HeaderSettingsReactWrapper from './site-nav-settings/react';
import { findPreviewBreakpoint } from './layout-settings/preview-breakpoint';
import controls from './control-types';
import * as settings from '../helpers/utilities/site-nav/settings';

// Only the first header of a page sticks and changes as the page scrolls.
function isPrimary(menu) {
  if (menu.elementPath.length !== 1) {
    return false;
  }
  for (let index = 0; index < menu.editor.children.length; index++) {
    if (menu.editor.children[index].type === 'site-nav') {
      return index === menu.elementPath[0];
    }
  }
  return false;
}

function findPreview(editor) {
  try {
    return findPreviewBreakpoint(ReactEditor.toDOMNode(editor, editor));
  } catch (exception) {
    return 'desktop';
  }
}

// Every change is one undo step; paths are relative to the header.
function buildConfiguration(menu) {
  const editor = menu.editor;
  function path(relative) { return menu.elementPath.concat(relative); }
  return {
    read() { return settings.read(editor, menu.elementPath); },
    node() { return Node.get(editor, menu.elementPath); },
    primary: isPrimary.bind(null, menu),
    close() { menu.onClose(); },
    write(changes) { settings.write(editor, menu.elementPath, changes); },
    setAttributes(relative, attributes) {
      HistoryEditor.withNewBatch(editor, function saveHeaderPart() {
        Transforms.setNodes(editor, attributes, { at: path(relative) });
      });
    },
    replace(relative, node) { settings.replace(editor, path(relative), node); },
    add(relative, node) { settings.add(editor, path(relative), node); },
    insert(relative, node) {
      HistoryEditor.withNewBatch(editor, function insertHeaderPart() {
        Transforms.insertNodes(editor, node, { at: path(relative) });
      });
    },
    remove(relative) { settings.remove(editor, path(relative)); },
    move(from, to) { settings.move(editor, path(from), path(to)); },
    preset(styles, changes) {
      HistoryEditor.withNewBatch(editor, function applyPreset() {
        const node = Node.get(editor, menu.elementPath);
        Transforms.setNodes(editor, { ...styles, properties: { ...node.properties, siteNav: { ...settings.read(editor, menu.elementPath), ...changes } } }, { at: menu.elementPath });
      });
    },
    resize() { menu.positionToolbar(); }
  };
}

function renderPanel(menu, panel) {
  return <HeaderSettingsReactWrapper panel={panel} breakpoint={findPreview(menu.editor)} value={menu.editor.children} configuration={buildConfiguration(menu)} />;
}

function renderItem(menu, name, Icon, panel) {
  return <Expandable Icon={Icon} name={name} activeMenuItemName={menu.activeMenuItemName} contentRenderer={renderPanel.bind(null, menu, panel)} positionToolbar={menu.positionToolbar} toggleActiveMenuItem={menu.toggleActiveMenuItem} />;
}

export function tryRenderHeaderSettings(menu) {
  if (!menu.customNodeControls.includes(controls.SITE_NAV_SETTINGS)) {
    return null;
  }
  return <>
    {renderItem(menu, 'Header layout', LayoutIcon, 'createHeaderLayout')}
    {renderItem(menu, 'Header content', ContentIcon, 'createHeaderContent')}
    {renderItem(menu, 'Header style', StyleIcon, 'createHeaderStyle')}
    {renderItem(menu, 'Header scrolling', ScrollingIcon, 'createHeaderScrolling')}
  </>;
}
